import React, { useEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from 'react-i18next';
import { setI18nLanguage } from "../../i18n/config";
import i18n from "../../i18n/config";
import { API_BASE } from "../../api";

// Mirrors Expense.PAYMENT_METHOD_CHOICES on the backend.
const PAYMENT_METHODS = [
  { value: "CASH", label: "Cash" },
  { value: "UPI", label: "UPI" },
  { value: "CHEQUE", label: "Cheque" },
  { value: "BANK_TRANSFER", label: "Bank Transfer" },
];

// Local-timezone "today" string (YYYY-MM-DD) for the date input's max
// attribute. toISOString() is UTC and can lag a day behind IST near midnight,
// which would wrongly block today's date for users.
const todayMax = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// orgId comes from whichever org the user currently has selected
// (same pattern as Dashboard.jsx / Timeline.jsx — passed down as a prop).
function AddExpensePage({ orgId, onSubmitExpense, onBack, onHome, onEditProfile }) {
  const { user, getAccessTokenSilently, logout } = useAuth0();
  const { t } = useTranslation('addExpense');
  const { t: tc } = useTranslation('common');

  const [form, setForm] = useState({
    purpose: "",
    amount: "",
    date: "",
    category: "",
    paymentMethod: "",
    bankAccount: "",
    description: "",
  });

  const [categories, setCategories] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [success, setSuccess] = useState(false);

  // ---- Language menu state ----
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langMenuRef = useRef(null);

  // ---- Profile dropdown state ----
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  useEffect(() => {
    if (!profileOpen) return;

    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [profileOpen]);

  // Close the language menu on outside click.
  useEffect(() => {
    if (!langMenuOpen) return;

    const handleClickOutside = (e) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target)) {
        setLangMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [langMenuOpen]);

  const handleLanguageChange = (lang) => {
    setI18nLanguage(lang);
    localStorage.setItem("zbookkeeping_language", lang);
    setLangMenuOpen(false);
  };

  const bankAccountRequired = form.paymentMethod && form.paymentMethod !== "CASH";

  // Refs for Enter-key auto-focus
  const refs = {
    purpose: useRef(null),
    amount: useRef(null),
    date: useRef(null),
    category: useRef(null),
    paymentMethod: useRef(null),
    bankAccount: useRef(null),
    description: useRef(null),
  };

  const fieldOrder = [
    "purpose",
    "amount",
    "date",
    "category",
    "paymentMethod",
    "bankAccount",
    "description",
  ];

  const handleKeyDown = (e, currentField) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const idx = fieldOrder.indexOf(currentField);
      const next = fieldOrder[idx + 1];
      if (next && refs[next]?.current) refs[next].current.focus();
    }
  };

  // Fetch categories + bank accounts from the real backend.
  useEffect(() => {
    if (!orgId) {
      setLoading(false);
      setLoadError(t('noOrgSelected'));
      return;
    }

    (async () => {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
        });

        const headers = { Authorization: `Bearer ${token}` };

        const [catRes, bankRes] = await Promise.all([
          fetch(`${API_BASE}/api/expenses/categories/`, { headers }),
          fetch(`${API_BASE}/api/expenses/bank-accounts/?org_id=${orgId}`, { headers }),
        ]);

        if (!catRes.ok || !bankRes.ok) {
          throw new Error("Failed to load expense data.");
        }

        const catData = await catRes.json();
        const bankData = await bankRes.json();

        setCategories(catData.results || []);
        setBankAccounts(bankData.results || []);
      } catch (e) {
        setLoadError(e.message || t('validation.loadError'));
      } finally {
        setLoading(false);
      }
    })();
  }, [orgId, getAccessTokenSilently,t]);

  const validate = () => {
    const e = {};

    if (!form.purpose) e.purpose = t('validation.purposeRequired');

    if (!form.amount || Number(form.amount) <= 0)
      e.amount = t('validation.amountRequired');

    if (!form.date) {
      e.date = t('validation.dateRequired');
    } else {
      const todayEnd = new Date();
      todayEnd.setHours(23, 59, 59, 999);

      if (new Date(form.date) > todayEnd) {
        e.date = t('validation.dateFuture');
      }
    }

    if (!form.category)
      e.category = t('validation.categoryRequired');

    if (!form.paymentMethod)
      e.paymentMethod = t('validation.paymentMethodRequired');

    if (bankAccountRequired && !form.bankAccount)
      e.bankAccount = t('validation.bankAccountRequired');

    return e;
  };

  const handleSubmit = () => {
    setSubmitError("");

    const validationErrors = validate();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    setShowConfirm(false);
    setSubmitting(true);
    setSubmitError("");

    const payload = {
      organization: orgId,
      purpose: form.purpose,
      amount: Number(form.amount),
      date: form.date,
      category: Number(form.category),
      payment_method: form.paymentMethod,
      bank_account: bankAccountRequired ? Number(form.bankAccount) : null,
      description: form.description,
    };

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
      });

      const res = await fetch(`${API_BASE}/api/expenses/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        // Standard error shape: { error, detail, code }
        setSubmitError(data.detail || data.error || tc('error'));
        return;
      }

      setSuccess(true);

      if (onSubmitExpense) onSubmitExpense(data);
    } catch (e) {
      setSubmitError(tc('networkError'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setSuccess(false);
    setSubmitError("");
    setErrors({});

    setForm({
      purpose: "",
      amount: "",
      date: "",
      category: "",
      paymentMethod: "",
      bankAccount: "",
      description: "",
    });
  };

  const handleLogout = () => {
    setProfileOpen(false);
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const handleEditProfile = () => {
    setProfileOpen(false);
    if (onEditProfile) onEditProfile();
  };

  // ── Success screen ────────────────────────────────────────────────────────
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center max-w-sm w-full">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
              <path
                d="M5 13l4 4L19 7"
                stroke="#16a34a"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <p className="text-base font-medium text-gray-900 mb-1">
            {t('successTitle')}
          </p>

          <p className="text-sm text-gray-500 mb-6">
            {t('successMessage')}
          </p>

          <button
            onClick={handleReset}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2.5 text-sm font-medium transition mb-2"
          >
            {t('addAnother')}
          </button>

          <button
            onClick={onBack}
            className="w-full border border-blue-600 text-blue-600 hover:bg-blue-50 rounded-lg px-4 py-2.5 text-sm font-medium transition"
          >
            {t('backToDashboard')}
          </button>
        </div>
      </div>
    );
  }

  const inputCls =
    "w-full bg-gray-100 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 border-0";

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 flex items-center gap-3 relative">
        <button
          onClick={onHome}
          aria-label="Home"
          title="Home"
          className="text-blue-600 hover:text-blue-700 flex-shrink-0"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            className="fill-current"
          >
            <path d="M12 3.2 3 10v10h6v-6h6v6h6V10l-9-6.8Z" />
          </svg>
        </button>

        <button
          onClick={onBack}
          aria-label="Back"
          className="text-blue-600 hover:text-blue-700"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path
              d="M19 12H5M5 12l7-7M5 12l7 7"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>

        <span className="text-sm font-medium text-gray-900 flex-1">
          {t('title')}
        </span>

        <div className="flex-1" />

        {/* ---- LANGUAGE TOGGLE ---- */}
        <div ref={langMenuRef} className="relative">
          <button
            onClick={() => setLangMenuOpen((open) => !open)}
            aria-label="Language menu"
            aria-expanded={langMenuOpen}
            className="text-xs font-medium text-gray-600 border border-gray-200 rounded-lg px-2.5 py-1.5 hover:bg-gray-50 transition"
          >
            {i18n.language === "ml" ? "മലയാളം" : "EN"}
          </button>

          {langMenuOpen && (
            <div className="absolute right-0 top-10 w-32 bg-white rounded-xl shadow-lg border border-gray-100 p-2 z-50">
              <button
                onClick={() => handleLanguageChange("en")}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm transition ${
                  i18n.language === "en"
                    ? "bg-blue-50 text-blue-700 font-medium"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                English
              </button>

              <button
                onClick={() => handleLanguageChange("ml")}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm transition ${
                  i18n.language === "ml"
                    ? "bg-blue-50 text-blue-700 font-medium"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                മലയാളം
              </button>
            </div>
          )}
        </div>

        {/* ---- USER AVATAR + PROFILE DROPDOWN ---- */}
        <div ref={profileRef} className="relative">
          <button
            onClick={() => setProfileOpen((open) => !open)}
            aria-label="Account menu"
            aria-expanded={profileOpen}
            className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-medium"
          >
            {user?.name?.charAt(0).toUpperCase() || "U"}
          </button>

          {profileOpen && (
            <div className="absolute right-0 top-11 w-64 bg-white rounded-xl shadow-lg border border-gray-100 p-4 z-50">
              <p className="text-sm text-gray-900 truncate">
                {user?.email}
              </p>

              <p className="text-sm text-gray-400 truncate mb-4">
                {user?.email}
              </p>

              <button
                onClick={handleEditProfile}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition mb-2"
              >
                {tc('editProfile')}
              </button>

              <button
                onClick={handleLogout}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition"
              >
                {tc('logout')}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-sm mx-auto px-4 py-8">
        <h1 className="text-center text-base font-medium text-blue-600 mb-6 uppercase tracking-wider">
          {t('title')}
        </h1>

        {/* Load / submit errors */}
        {loadError && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 text-sm text-red-600">
            {loadError}
          </div>
        )}

        {submitError && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 text-sm text-red-600">
            {submitError}
          </div>
        )}

        {/* Purpose */}
        <div className="mb-4">
          <p className="text-xs text-gray-500 uppercase tracking-widest mb-1.5">
            {t('purpose')} <span className="text-red-500">*</span>
          </p>

          <input
            ref={refs.purpose}
            type="text"
            value={form.purpose}
            onChange={(e) => setForm({ ...form, purpose: e.target.value })}
            onKeyDown={(e) => handleKeyDown(e, "purpose")}
            placeholder={t('purpose')}
            className={inputCls}
          />

          {errors.purpose && (
            <p className="text-xs text-red-500 mt-1">
              {errors.purpose}
            </p>
          )}
        </div>

        {/* Amount */}
        <div className="mb-4">
          <p className="text-xs text-gray-500 uppercase tracking-widest mb-1.5">
            {t('amount')} <span className="text-red-500">*</span>
          </p>

          <input
            ref={refs.amount}
            type="number"
            min="1"
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            onKeyDown={(e) => handleKeyDown(e, "amount")}
            placeholder="0"
            className={inputCls}
          />

          {errors.amount && (
            <p className="text-xs text-red-500 mt-1">
              {errors.amount}
            </p>
          )}
        </div>

        {/* Date */}
        <div className="mb-4">
          <p className="text-xs text-gray-500 uppercase tracking-widest mb-1.5">
            {t('date')} <span className="text-red-500">*</span>
          </p>

          <input
            ref={refs.date}
            type="date"
            max={todayMax()}
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            onKeyDown={(e) => handleKeyDown(e, "date")}
            className={inputCls}
          />

          {errors.date && (
            <p className="text-xs text-red-500 mt-1">
              {errors.date}
            </p>
          )}
        </div>

        {/* Category — from API (values are ExpenseCategory ids, labels are names) */}
        <div className="mb-4">
          <p className="text-xs text-gray-500 uppercase tracking-widest mb-1.5">
            {t('category')} <span className="text-red-500">*</span>
          </p>

          <select
            ref={refs.category}
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            onKeyDown={(e) => handleKeyDown(e, "category")}
            className={inputCls}
            disabled={loading}
          >
            <option value="">
              {loading ? tc('loading') : t('selectCategory')}
            </option>

            {categories.length > 0 ? (
              categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))
            ) : (
              <option disabled>
                {t('noCategories')}
              </option>
            )}
          </select>

          {errors.category && (
            <p className="text-xs text-red-500 mt-1">
              {errors.category}
            </p>
          )}
        </div>

        {/* Payment Method */}
        <div className="mb-4">
          <p className="text-xs text-gray-500 uppercase tracking-widest mb-1.5">
            {t('paymentMethod')} <span className="text-red-500">*</span>
          </p>

          <select
            ref={refs.paymentMethod}
            value={form.paymentMethod}
            onChange={(e) => {
              const value = e.target.value;

              setForm({
                ...form,
                paymentMethod: value,
                bankAccount: value === "CASH" ? "" : form.bankAccount,
              });
            }}
            onKeyDown={(e) => handleKeyDown(e, "paymentMethod")}
            className={inputCls}
          >
            <option value="">
              {t('selectMethod')}
            </option>

            {PAYMENT_METHODS.map((pm) => (
              <option key={pm.value} value={pm.value}>
                {pm.label}
              </option>
            ))}
          </select>

          {errors.paymentMethod && (
            <p className="text-xs text-red-500 mt-1">
              {errors.paymentMethod}
            </p>
          )}
        </div>

        {/* Bank Account — from API */}
        <div className="mb-4">
          <p className="text-xs text-gray-500 uppercase tracking-widest mb-1.5">
            {t('bankAccount')}

            {!bankAccountRequired && (
              <span className="text-gray-400 normal-case">
                {" "}
                {t('bankAccountCashNote')}
              </span>
            )}

            {bankAccountRequired && (
              <span className="text-red-500"> *</span>
            )}
          </p>

          {loading ? (
            <div className={`${inputCls} text-gray-400`}>
              {t('loadingAccounts')}
            </div>
          ) : (
            <select
              ref={refs.bankAccount}
              value={form.bankAccount}
              onChange={(e) =>
                setForm({ ...form, bankAccount: e.target.value })
              }
              onKeyDown={(e) => handleKeyDown(e, "bankAccount")}
              className={inputCls}
              disabled={!bankAccountRequired}
            >
              <option value="">
                {t('selectBankAccount')}
              </option>

              {bankAccounts.length > 0 ? (
                bankAccounts.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name} (₹{b.balance})
                  </option>
                ))
              ) : (
                <option disabled>
                  {t('noBankAccounts')}
                </option>
              )}
            </select>
          )}

          {errors.bankAccount && (
            <p className="text-xs text-red-500 mt-1">
              {errors.bankAccount}
            </p>
          )}
        </div>

        {/* Description (optional) */}
        <div className="mb-8">
          <p className="text-xs text-gray-500 uppercase tracking-widest mb-1.5">
            {t('description')}{" "}
            <span className="text-gray-400 normal-case">
              {t('descriptionOptional')}
            </span>
          </p>

          <textarea
            ref={refs.description}
            value={form.description}
            onChange={(e) =>
              setForm({ ...form, description: e.target.value })
            }
            placeholder={t('descriptionPlaceholder')}
            rows={3}
            className={`${inputCls} resize-none`}
          />
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={submitting || loading}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed text-white rounded-lg px-4 py-3 text-sm font-medium transition"
        >
          {submitting ? tc('submitting') : t('submitButton')}
        </button>
      </div>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 mx-4 w-full max-w-xs text-center shadow-xl">
            <p className="text-sm font-medium text-gray-900 mb-2">
              Are you sure?
            </p>

            <p className="text-xs text-gray-500 mb-6">
              This expense will be submitted for approval.
            </p>

            <div className="flex flex-col gap-3">
              <button
                onClick={handleConfirm}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition"
              >
                Confirm Expense
              </button>

              <button
                onClick={() => setShowConfirm(false)}
                className="w-full bg-red-500 hover:bg-red-600 text-white rounded-lg py-2.5 text-sm font-medium transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AddExpensePage;